/**
 * **Schema discovery, on this device** — `POST /api/relay/discover`'s first client caller ever.
 *
 * ── What was already built, and what was missing ─────────────────────────────────────────────
 *
 * `planDiscovery` (`packages/connectors/src/plan.ts`), the relay route, and the drift maths
 * (`schemaFingerprint` / `diffSchema`) all shipped and are tested. Grep found **zero callers
 * outside tests**: no screen in any of the four apps asks a connected system what fields it has.
 * The one field list a client can see today is for *custom objects only*, so a Contact, a Lead, an
 * Account and a driveItem are all opaque.
 *
 * Drift did have one consumer — the six-hourly health sweep computes it and writes a
 * `source.schema_drift` audit row. That is a record, not an answer: nobody is told, and reading it
 * means opening the audit trail and knowing to look.
 *
 * ── Why the FIRST look reports no drift, rather than none ────────────────────────────────────
 *
 * ⚠️ `diffSchema` needs a *previous*. On a connection nobody has discovered before there is nothing
 * to compare against, and the honest answer is **"we recorded your schema; drift is answerable from
 * the next look"** — not a drift of zero, which claims a comparison that never happened. That
 * distinction is the same one `insufficient_data` carries everywhere else in this product.
 *
 * ── The limit this inherits, stated rather than discovered ───────────────────────────────────
 *
 * ⚠️ **Names and labels only.** `diffSchema`'s own header says so: the discovery contract carries no
 * types, so a field retyped from text to number is **undetectable**. An audit that quietly implied
 * otherwise would be worse than one that says what it cannot see.
 */
import { diffSchema, schemaFingerprint } from '@dedupr/connectors';
import { accessToken } from '../auth.js';
import { openLocalStore, type LocalStore, type StoredSchema } from './store.js';

export interface DiscoveryOutcome {
  connectionId: string;
  objectId: string;
  provider: string;
  fields: string[];
  /**
   * What changed since the last look — **`null` when there was no last look**, which is not the
   * same as nothing having changed. Every renderer must keep the two apart.
   */
  drift: { added: string[]; removed: string[] } | null;
  /** When the previous field list was recorded, so "since when" is answerable. */
  comparedAgainst: string | null;
}

export type DiscoveryResult =
  | { ok: true; outcome: DiscoveryOutcome }
  /** The relay's own refusal — `no_discovery_spec`, `not_connectable`, a rate-limit — kept intact. */
  | { ok: false; code: string; detail: string };

/**
 * Ask a connection what fields one of its objects has, record it, and report what moved.
 *
 * The store write happens **after** a successful read and **before** the outcome is returned, so a
 * discovery that the caller never renders still advances the baseline. That is deliberate: the
 * value of a baseline is that it exists, not that somebody looked at it.
 */
export async function discoverSchema(
  connectionId: string,
  providerId: string,
  objectId: string,
  store?: LocalStore
): Promise<DiscoveryResult> {
  let response: Response;
  try {
    const token = await accessToken();
    response = await fetch('/api/relay/discover', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ connection: connectionId, provider: providerId, object: objectId }),
    });
  } catch {
    return { ok: false, code: 'unreachable', detail: 'The connector service could not be reached.' };
  }

  const body = (await response.json().catch(() => null)) as
    | { fields?: { name: string; label?: string }[]; code?: string; detail?: string; title?: string }
    | null;

  if (!response.ok) {
    return {
      ok: false,
      code: String(body?.code ?? body?.title ?? 'refused'),
      detail: String(body?.detail ?? `The schema could not be read (${response.status}).`),
    };
  }

  /* ⚠️ A 200 with no `fields` array is not an empty schema — it is a shape we do not understand,
     and reporting "this object has no fields" would be a claim about the client's system. */
  if (!Array.isArray(body?.fields)) {
    return {
      ok: false,
      code: 'unreadable',
      detail: 'The connector service answered, but not with a field list we could read.',
    };
  }

  const fields = body.fields.map((f) => String(f.name)).sort();
  const fingerprint = schemaFingerprint(fields);
  const id = `${connectionId}:${objectId}`;

  const owned = store ?? (await openLocalStore());
  try {
    const previous = (await owned.listSchemas()).find((s) => s.id === id);
    /* ⚠️ Compared on the FINGERPRINT, not on the arrays. `schemaFingerprint` sorts and
       length-prefixes, so two orderings of the same fields are one schema — a provider that returns
       its fields in a different order on Tuesday is not drift. */
    const drift =
      previous === undefined
        ? null
        : previous.fingerprint === fingerprint
          ? { added: [], removed: [] }
          : diffSchema(previous.fields, fields);

    const row: StoredSchema = {
      id,
      connection_id: connectionId,
      object_id: objectId,
      provider: providerId,
      fields,
      fingerprint,
      discovered_at: new Date().toISOString(),
    };
    await owned.putSchema(row);

    return {
      ok: true,
      outcome: {
        connectionId,
        objectId,
        provider: providerId,
        fields,
        drift,
        comparedAgainst: previous?.discovered_at ?? null,
      },
    };
  } finally {
    /* Only close what this function opened. A caller that passed its own store is still using it. */
    if (!store) owned.close();
  }
}
